import AppContext from 'App/AppContext';
import { IPMessageBar, IPMessageBarButton, IPSpinner, IPTooltipHost } from 'baseComponents';
import { IPMessageBarType } from 'baseComponents/MessageBar/IPMessageBar';
import { IPStack } from 'baseComponents/Stack/IPStack';
import SupportTicketModal from 'components/Dialogs/SupportTicketModal';
import { freshdeskWidgetOpen, freshdeskWidgetSetContactInfo, freshdeskWidgetSetTranslations } from 'globalFunctions';
import { globalStackTokensGapSmall } from 'globalStyles';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { safeStringify } from 'utils/stringify';
import { isAbortOrCancelError, isAuthReloadError, isLoginError, isNetworkError } from './ErrorMessageHelper';
import { getErrorMessageText, saveErrorToApi } from './ErrorMessageService';

export enum ICustomErrorActionType {
  None = 0,
  Refresh = 1,
  Dismiss = 2,
}

interface IErrorMessageProps {
  error: AppError | undefined;
  setError?: Function;
  //when provided, this function is called to refresh the app instead of reloading the page and calling setError
  customAction?: (actionType: ICustomErrorActionType) => Promise<void>;
}

export const ErrorMessage = (props: IErrorMessageProps) => {
  const history = useHistory();
  const appContext = useContext(AppContext);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [showTicketModal, setShowTicketModal] = useState<boolean>(false);
  // 'exceptions' is required for typed ControllerException rendering (e.g. INTEGRATION0001/2/3
  // via AppError.getControllerExceptionMsg). ErrorMessage is the global error renderer so it
  // must always have the typed-error namespace loaded, regardless of which screen mounted it.
  const { t } = useTranslation(['translation', 'exceptions']);
  const [err, setErr] = useState<AppError | undefined>(undefined);

  useEffect(() => {
    if (props.error) {
      if (!err?.isEqual(props.error)) {
        setErr(props.error);
        logger.error(props.error);
        saveErrorToApi(props.error, appContext, t);
      }
    } else {
      setErr(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error]);

  const reloadPage = async () => {
    try {
      if (isRefreshing) return;
      setIsRefreshing(true);
      await appContext.globalDataCache.refresh();
      history.push('/reload');
      history.goBack();
    } catch (err) {
      logger.error(new AppError('Error while refreshing the page. ' + safeStringify(err)));
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshPageButton = (label: string) => {
    return (
      <div>
        <IPMessageBarButton
          disabled={isRefreshing}
          onRenderMenuIcon={() => {
            if (isRefreshing) {
              return <IPSpinner />;
            } else {
              return null;
            }
          }}
          onClick={async () => {
            if (props.customAction) {
              await props.customAction(ICustomErrorActionType.Refresh);
            } else {
              await reloadPage();
              if (props.setError) props.setError(undefined);
            }
          }}
        >
          {label}
        </IPMessageBarButton>
      </div>
    );
  };

  const openSupportTicket = () => {
    // Mirror the existing Contact-support pattern used elsewhere
    // (Store.tsx, HelpNavItem.tsx, AddWidgetPanel.tsx, GlobalSearchResults.tsx):
    // prefer the HubSpot ticket modal when enabled, fall back to the Freshdesk widget.
    if (Config.isHubSpotSupportEnabled()) {
      setShowTicketModal(true);
    } else {
      freshdeskWidgetOpen();
      freshdeskWidgetSetContactInfo(appContext.user.name, appContext.user.email);
      freshdeskWidgetSetTranslations();
    }
  };

  const billingSubscriptionActions = () => {
    const isManager = hasUserFeatureGenericManager(appContext);
    const primaryStyles = { label: { fontWeight: 600 as const } };
    // Both buttons use IPMessageBarButton -- matches Fluent's MessageBar action
    // pattern where actions are intentionally subdued so the alert color stays
    // the visual emphasis. Hierarchy comes from order + a bolder label on the
    // action the user should take first: Manage subscription for managers
    // (self-resolve), Contact support for non-managers (only action available).
    const contactSupportIsPrimary = !isManager;

    return (
      <IPStack
        horizontal
        verticalAlign="center"
        tokens={globalStackTokensGapSmall}
        // 6px vertical padding: in single-line mode the MessageBar action row
        // sits flush against the bar edges without breathing room otherwise.
        styles={{ root: { padding: '6px 0' } }}
      >
        {isManager && (
          <IPMessageBarButton
            onClick={() => {
              history.push('/admin/subscription');
              if (props.setError) props.setError(undefined);
            }}
            styles={primaryStyles}
          >
            {t('translation:Errors.Billing.ManageSubscription')}
          </IPMessageBarButton>
        )}
        <IPMessageBarButton onClick={openSupportTicket} styles={contactSupportIsPrimary ? primaryStyles : undefined}>
          {t('translation:Errors.Billing.ContactSupport')}
        </IPMessageBarButton>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (props.error === undefined) {
    // Keep the support ticket modal mounted even after the underlying error is
    // dismissed -- otherwise an in-progress ticket form unmounts and loses its
    // state when the user closes the error banner first.
    return showTicketModal ? (
      <SupportTicketModal isOpen={showTicketModal} onDismiss={() => setShowTicketModal(false)} />
    ) : null;
  }

  // Some ControllerException codes are user-actionable: instead of only
  // surfacing the message, we render a button inside the error bar that takes
  // the user to where they can resolve it (e.g. BILLING0001 -> Manage
  // subscription / Contact support). Each entry below is one such override;
  // codes without an entry render the default warning bar with the translated
  // message. All fields are optional.
  type ControllerExceptionPresentation = {
    mbType?: IPMessageBarType;
    renderAction?: () => JSX.Element;
    isMultiline?: boolean;
  };
  const controllerExceptionPresentations: Partial<
    Record<ControllerExceptionCodes, ControllerExceptionPresentation>
  > = {
    [ControllerExceptionCodes.Code_Billing_SubscriptionRequired]: {
      mbType: IPMessageBarType.blocked,
      renderAction: billingSubscriptionActions,
      isMultiline: false,
    },
  };

  const msg = getErrorMessageText(props.error, t);
  let action: JSX.Element | undefined;
  let mbType: IPMessageBarType = IPMessageBarType.error;
  let isMultiline = true;

  const showDismiss = props.setError !== undefined || props.customAction !== undefined;

  //in case of network/connectivity errors, show a popup to refresh the browser tab
  if (isNetworkError(props.error) || isAbortOrCancelError(props.error) || isLoginError(props.error)) {
    mbType = IPMessageBarType.warning;
    action = refreshPageButton(t('translation:Errors.NetworkError.Action'));
  } else {
    //Determine message and action
    switch (props.error.code) {
      case '429': //too many requests
        mbType = IPMessageBarType.warning;
        action = refreshPageButton(t('translation:Errors.TooManyRequests.Action'));
        break;
      case '404': //not found: another user has deleted the object
        mbType = IPMessageBarType.warning;
        action = refreshPageButton(t('translation:Errors.DatabaseNotFound.Action'));
        break;
      case '409': //conflict: another user has updated/deleted the object or relationship
        mbType = IPMessageBarType.warning;
        action = refreshPageButton(t('translation:Errors.DatabaseConflict.Action'));
        break;
      case '401':
      case '403':
        //special case: when the user is in the admin module, navigate away to dashboard
        //when the user is in the admin module and switches to another tenant, the app can still refresh the current scene and produce 403
        //with the new account which may not have admin permissions
        if (isAuthReloadError(props.error)) {
          window.location.href = '/';
          window.location.reload();

          return null;
        }
        mbType = IPMessageBarType.blocked;
        break;
      case '400':
      case '500':
        if (props.error.debug && props.error.debug.indexOf('AccessDenied') >= 0) {
          mbType = IPMessageBarType.blocked;
        } else if (AppError.isControllerException(props.error)) {
          const code = AppError.getControllerExceptionCode(props.error) as
            | ControllerExceptionCodes
            | undefined;
          const presentation = code ? controllerExceptionPresentations[code] : undefined;
          mbType = presentation?.mbType ?? IPMessageBarType.warning;
          if (presentation?.renderAction) action = presentation.renderAction();
          if (presentation?.isMultiline !== undefined) isMultiline = presentation.isMultiline;
        }
        break;
      case '999':
        //unhandled exception
        mbType = IPMessageBarType.severeWarning;
        break;
    }
  }

  return (
    <Fragment>
      <IPTooltipHost content={!Config.isProd() && !Config.isTest() ? props.error.debug : undefined}>
        <IPMessageBar
          messageBarType={mbType}
          onDismiss={
            !showDismiss
              ? undefined
              : async () => {
                  if (props.customAction) {
                    await props.customAction(ICustomErrorActionType.Dismiss);
                  } else {
                    if (props.setError) props.setError(undefined);
                  }
                }
          }
          isMultiline={isMultiline}
          actions={action}
        >
          <b>{msg}</b>
        </IPMessageBar>
      </IPTooltipHost>
      <SupportTicketModal isOpen={showTicketModal} onDismiss={() => setShowTicketModal(false)} />
    </Fragment>
  );
};
